import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Header } from './components/Header';
import { Viewport } from './components/Viewport';
import { ConfigPanel } from './components/ConfigPanel';
import { MobileFooter } from './components/MobileFooter';
import { TabType } from './types';
import { Save, ChevronRight, ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ConflictDialog } from './components/ConflictDialog';
import { OptionsDialog } from './components/OptionsDialog';
import { PackageDetailsDialog } from './components/PackageDetailsDialog';
import { DealerSelectorDialog, DEALER_AUTONTEL_ID_MAP } from './components/DealerSelectorDialog';
import { LocalMatchesDialog } from './components/LocalMatchesDialog';
import SpecDetails from './components/SpecDetails/SpecDetails';
import SaveDialog from './components/SaveDialog';
import { ShareModal } from './components/ShareModal';
import { useRTL, useTranslation } from './utils/translations';
import { dispatchAutoIntelEvent, AutoIntelEvents, getInventoryRecommendations, getAutoIntelRPOCode } from './utils/autointel';
import { useLogEvent, AnalyticsIds, useAnalyticsSegmentationContext, useLoadingAnalytics, useRegisterDefaultSegmentations, getLoadInfo } from './utils/useAnalytics';
import {
  CatchEmittedEvents,
  useCheckLoading,
  useGetConfiguration,
  useConflicts,
  useLoadAssets,
  useAccessoryStore,
  SetActiveAccessory,
} from '@adloid-technologies/discover-sdk';
import { useConfigurationStore } from '@adloid-technologies/discover-sdk/dist/esm/store/configuration-store';
import { ConfigurationStore } from '@adloid-technologies/discover-sdk/dist/esm/store/configuration-store';
import { useChangeDefaultConfigOnVariantChange } from '@adloid-technologies/discover-sdk/dist/esm/hooks/useChangeDefaultConfigOnVariantChange';
import { AddPackage, RemovePackage } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/packages';
import { AddMultipleAccessoriesWithLoading, RemoveMultipleAccessoriesWithLoading } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/accessory';
import { LoaderState } from '@adloid-technologies/discover-sdk/dist/esm/store/loader-store';
import { EmitEventErrors } from '@adloid-technologies/discover-sdk/dist/esm/EventEmitter/event-emmiter.types';
import { setLightingWithLoading, setPostProcessingWithLoading } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/lighting-postprocessing';
import { accessoriesCoreWebgl, setCameraCoreWebgl } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/messages';
import { HandleInteraction } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/interactions';
import { InteractionStore } from '@adloid-technologies/discover-sdk/dist/esm/store/interactions-store';
import { FeatureStore } from '@adloid-technologies/discover-sdk/dist/esm/store/feature-store';
import { usePackageStore } from '@adloid-technologies/discover-sdk/dist/esm/store/package-store';
import { uimetadata, metadata, assetSrc } from './utils/metadata';
import { applyGlowMesh } from './utils/glowMesh';
import { getPrice, getDisplayPrice } from './utils/pricing';
import { isLocalMatchesEnabled } from './utils/common';
import LoadingProgress from './LoadingProgress';
import { getSelectedJourney, getJourney } from './utils/selectedJourney';
import { getShareUrl, getSharedVariantId } from './utils/shareUrl';
import { canUseNativeShare, shareNatively } from './utils/nativeShare';
import { LoadingScreen } from './components/LoadingScreen/LoadingScreen';
import { EventConstants } from './utils/eventConstants';
import { useColorPackageLocks } from './hooks/useColorPackageLocks';

const TABS: TabType[] = ['CONFIG', 'MODEL', 'EXTERIOR', 'INTERIOR', 'OPTIONS', 'SUMMARY'];

// Delay before re-asserting the exterior camera after an interior→exterior
// switch, so it lands after the interior camera preset has finished settling.
const EXTERIOR_CAMERA_REAPPLY_MS = 500;

// Corvette accessory groups that hold more than one hidden isDefault, each scoped to a
// different set of relevantVariants. The SDK picks the first one on deselect regardless
// of variant, so these need the app-side handling below.
const VARIANT_SCOPED_DEFAULT_GROUPS = ['Caliper', 'Logo', 'Roof', 'BodyAccentPkg'];

/** Re-keys model.accessories so the current variant's hidden base-state defaults come
 *  first. The SDK's getDefaultAccessoryForGroupId() returns the FIRST isDefault entry in
 *  a group in object order and ignores relevantVariants, so on deselect it was handing
 *  back another family's part (a Stingray caliper deselect loaded the GS one, which
 *  RemoveAccessory awaits into the scene — hence the visible flash before the correction
 *  below swapped it). Fixing the scan order makes the SDK's own pick right, so nothing
 *  wrong is ever applied.
 *
 *  Deliberately does NOT touch isDefault: useInitialiseVariant() reads defaultAccessoryIDs
 *  from the configuration-store setter, i.e. before React re-renders with the new
 *  variantID, so un-flagging a default would race that and can drop a variant's base state.
 *  Only display:false entries move, and getAccessoryIdsByCategory filters those out, so
 *  the order of UI-visible cards is unchanged. */
const hoistVariantScopedDefaults = (variantID: string) => {
  const model = (metadata as any)?.models?.[uimetadata.config.model];
  if (uimetadata.config.model !== 'Corvette' || !variantID || !model?.accessories) return;
  const accessories: Record<string, any> = model.accessories;
  const firstIds = VARIANT_SCOPED_DEFAULT_GROUPS
    .map((groupId) =>
      Object.keys(accessories).find((id) => {
        const acc = accessories[id];
        return acc?.groupId === groupId && acc.isDefault && acc.relevantVariants?.includes(variantID);
      }),
    )
    .filter((id): id is string => !!id);
  if (!firstIds.length) return;
  const reordered: Record<string, any> = {};
  firstIds.forEach((id) => { reordered[id] = accessories[id]; });
  Object.keys(accessories).forEach((id) => { if (!(id in reordered)) reordered[id] = accessories[id]; });
  model.accessories = reordered;
};

/** Returns tabs filtered by metadata toggleTabs for the given variant & viewport. */
function getVisibleTabs(variantId: string, modelId: string, isMobile: boolean): TabType[] {
  const variantData = (uimetadata.models as any)[modelId]?.variants?.[variantId] ?? {};
  const tabToggles = variantData.toggleTabs ?? {};
  return TABS.filter((tab) =>
    isMobile ? tabToggles[tab]?.mobile !== false : tabToggles[tab]?.desktop !== false,
  );
}

/** Returns the label for the Next button based on what the next visible tab is. */
function getNextButtonLabel(currentTab: TabType, visibleTabs: TabType[], t: (key: string, fallback: string) => string): string {
  const idx = visibleTabs.indexOf(currentTab);
  const nextTab = idx >= 0 && idx < visibleTabs.length - 1 ? visibleTabs[idx + 1] : null;
  if (!nextTab) return t('staticTranslations.common.proceed', 'Proceed');
  if (nextTab === 'MODEL')    return t('staticTranslations.common.next', 'Next');
  if (nextTab === 'EXTERIOR') return t('staticTranslations.common.exterior', 'Exterior');
  if (nextTab === 'INTERIOR') return t('staticTranslations.common.interior', 'Interior');
  if (nextTab === 'OPTIONS')  return t('staticTranslations.common.options', 'Options');
  if (nextTab === 'SUMMARY')  return t('staticTranslations.common.summary', 'Summary');
  return t('staticTranslations.common.proceed', 'Proceed');
}

function useMobileLandscape() {
  const [isLandscape, setIsLandscape] = useState(false);
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setIsLandscape(w > h && Math.min(w, h) <= 600);
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return isLandscape;
}

export default function App() {
  const isMobileLandscape = useMobileLandscape();
  const { isRTL } = useRTL();
  const { t } = useTranslation();
  const { variant: isLoading } = useCheckLoading();
  const { variantID, accessoriesIDs, packageIDs, view, exteriorColorID, interiorColorID } = useGetConfiguration();
  // Render-phase on purpose: this must land before any effect in the tree, and before the
  // user can click anything, so the SDK reads the re-keyed order on the first deselect.
  useMemo(() => hoistVariantScopedDefaults(variantID), [variantID]);
  // Per-variant background dome skus: the SDK's configuration-store already reloads the dome
  // on backgroundId change using metadata.backgrounds[id].sku (model-level, single sku). For
  // models that define per-variant skus (e.g. Corvette GSX), sync those skus into the metadata
  // whenever the variant changes, so the SDK's own switch loads the right dome — no second
  // fetch. defaultSkus handles the dome on variant load; backgroundId is unchanged then.
  useEffect(() => {
    const mId = uimetadata.config.model;
    const perVariant = (uimetadata.models as any)?.[mId]?.backgroundVariantSkus?.[variantID];
    const graphicsBgs = (metadata as any)?.models?.[mId]?.backgrounds;
    if (!perVariant || !graphicsBgs) return;
    // Each entry is a sku string, or { sku, project } when the trim's dome lives in a
    // different GU project than the tile's base one (e.g. Sierra 2379 tile 2).
    Object.keys(graphicsBgs).forEach((bgId) => {
      const override = perVariant[bgId];
      if (!override) return;
      if (typeof override === 'string') {
        graphicsBgs[bgId].sku = override;
      } else {
        graphicsBgs[bgId].sku = override.sku;
        if (override.project) graphicsBgs[bgId].project = override.project;
      }
    });
  }, [variantID]);
  // The SDK swaps the dome on background change but does NOT reload lighting/post-processing
  // from the dome asset. Do it here (app-side; the SDK is intentionally not modified) once the
  // NEW background has finished loading — i.e. when LoaderState.isBackgroundLoading goes
  // true -> false. The dome sku is taken from the current variant's defaultSkus (id:'dome').
  useEffect(() => {
    return LoaderState.subscribe(
      (s: any) => s.isBackgroundLoading,
      (loading: boolean, prevLoading: boolean) => {
        if (!(prevLoading && !loading)) return; // only on load-complete transition
        const mId = uimetadata.config.model;
        const vId = (useConfigurationStore.getState() as any)?.configuration?.variantID;
        const defaultSkus = (metadata as any)?.models?.[mId]?.variants?.[vId]?.defaultSkus ?? [];
        const sku = defaultSkus.find((s: any) => s.id === 'dome')?.sku;
        if (sku) {
          setLightingWithLoading(sku);
          setPostProcessingWithLoading(sku);
        }
      },
    );
  }, []);
  // Corvette Stingray: the spoiler carbon-flash finish depends on BOTH the Carbon Flash
  // mirror package (accOrvmCarbonFlash) and the spoiler shape (accSpoilerLowProfile vs the
  // default). The finish interactions aren't reported by get-if-interaction-active, so this
  // can't be driven by metadata conditions — coordinate it from accessoriesIDs (reliable).
  const prevSpoilerCfRef = useRef<string>('');
  useEffect(() => {
    if (uimetadata.config.model !== 'Corvette') return;
    const ids = accessoriesIDs ?? [];
    const carbonFlash = ids.includes('accOrvmCarbonFlash');
    const lowProfile = ids.includes('accSpoilerLowProfile');
    const key = `${carbonFlash}|${lowProfile}`;
    if (prevSpoilerCfRef.current === key) return;
    prevSpoilerCfRef.current = key;
    const msg = (message: string) => [{ message, identifiers: ['common_exterior'], condition: '', isDefault: false, isStop: false }];
    if (carbonFlash) {
      void accessoriesCoreWebgl(msg(lowProfile ? 'Low_Profile_TVS_Carbon_Flash' : 'T0A_Carbon_Flash') as any, true);
      void accessoriesCoreWebgl(msg(lowProfile ? 'T0A_Carbon_Flash' : 'Low_Profile_TVS_Carbon_Flash') as any, false);
    } else {
      void accessoriesCoreWebgl(msg('T0A_Carbon_Flash') as any, false);
      void accessoriesCoreWebgl(msg('Low_Profile_TVS_Carbon_Flash') as any, false);
    }
  }, [accessoriesIDs]);
  // Corvette: these groups carry ONE hidden isDefault per variant family (StingRay vs
  // GS X, coupe vs convertible) — the black/dark-gray caliper, the black logo badge, the
  // standard roof, the body-colour accent. On deselect the SDK re-applies
  // getDefaultAccessoryForGroupId(), which returns the FIRST isDefault in the group and
  // ignores relevantVariants, so dropping an option loads another family's default (e.g.
  // deselecting a Stingray caliper applied the GS Dark Gray Metallic one). Coordinate
  // app-side: whenever a foreign default turns up active, swap it for the one whose
  // relevantVariants cover the current variant.
  // Runs off isAccessoryLoading going true -> false rather than off an accessoriesIDs
  // effect: the SDK adds the bad default *inside* that loading window (mid-await), so an
  // accessoriesIDs effect would interleave with the removal still in flight. Waiting for
  // the operation to settle means this sees the final state and issues one clean swap.
  useEffect(() => {
    if (uimetadata.config.model !== 'Corvette') return;
    let correcting = false; // our own apply/remove re-enters this subscription
    return LoaderState.subscribe(
      (s: any) => s.isAccessoryLoading,
      (loading: boolean, prevLoading: boolean) => {
        if (!(prevLoading && !loading)) return; // only when an accessory op completes
        if (correcting || LoaderState.getState().isVariantLoading) return;
        const ids: string[] = (useAccessoryStore.getState() as any)?.accessoriesIDs ?? [];
        if (!ids.length) return;
        const vId: string = (useConfigurationStore.getState() as any)?.configuration?.variantID ?? '';
        const accessories: Record<string, any> = (metadata as any)?.models?.[uimetadata.config.model]?.accessories ?? {};
        const toApply: string[] = [];
        const toRemove: string[] = [];
        VARIANT_SCOPED_DEFAULT_GROUPS.forEach((groupId) => {
          const group = Object.entries(accessories).filter(([, acc]) => acc?.groupId === groupId);
          // Strictly corrective: act only on a default that is ACTIVE yet belongs to another
          // variant. Never apply a missing default — doing that during startup re-requests
          // assets the variant load is still fetching, which double-loads them and stalls it.
          const wrong = group
            .filter(([id, acc]) => acc.isDefault && ids.includes(id) && !acc.relevantVariants?.includes(vId))
            .map(([id]) => id);
          if (!wrong.length) return;
          const correct = group.find(([, acc]) => acc.isDefault && acc.relevantVariants?.includes(vId))?.[0];
          if (!correct) return;
          // Applying the correct default removes the foreign one via the single-select group;
          // if it is already active, applying is a no-op so the foreign one needs removing
          // explicitly. Removing an isDefault accessory never re-applies a group default.
          if (ids.includes(correct)) toRemove.push(...wrong);
          else toApply.push(correct);
        });
        if (!toApply.length && !toRemove.length) return;
        correcting = true;
        void (async () => {
          try {
            if (toApply.length) await AddMultipleAccessoriesWithLoading(toApply, false);
            if (toRemove.length) await RemoveMultipleAccessoriesWithLoading(toRemove, 'accessory', false);
          } finally {
            correcting = false;
          }
        })();
      },
    );
  }, []);
  // Corvette: auto-open the roof when a convertible variant finishes loading (top down).
  // On every variant load, force ALL interactions (doors, roof, boot) closed — in the
  // graphics AND the InteractionStore (UI) — so nothing carries over open from the
  // previous variant and the toggles stay in sync with the model. HandleInteraction(false)
  // flips the store state too, so UI + graphics agree. Convertibles are the one exception:
  // they load "top down", so after closing everything else we open the roof.
  useEffect(() => {
    return LoaderState.subscribe(
      (s: any) => s.isVariantLoading,
      (loading: boolean, prevLoading: boolean) => {
        if (!(prevLoading && !loading)) return; // variant load just completed
        const { interactions } = InteractionStore.getState();
        Object.keys(interactions)
          .filter((iid) => interactions[iid].state)
          .forEach((iid) => { void HandleInteraction(iid, false, { snapToLastFrame: true }); });
        // Convertibles load with the roof open (top down) — open it after the close-all.
        const vId: string = (useConfigurationStore.getState() as any)?.configuration?.variantID ?? '';
        if (uimetadata.config.model === 'Corvette' && vId.toLowerCase().includes('conv')) {
          void HandleInteraction('intrRoof', true);
        }
      },
    );
  }, []);
  // Showcase-feature autoplay drives the camera onto parts, zooming past the model's
  // normal orbit radius. While a feature is actively PLAYING, drop the radius limits
  // (upper/lower = null) so the cinematic camera can move freely; when it's PAUSED (or
  // otherwise not actively playing), re-send the model-load orbit LIMITS (alpha/beta/radius)
  // — only these, not the camera position/fov/target — so manual orbit is bounded again.
  useEffect(() => {
    if (uimetadata.config.model !== 'Corvette') return;
    const cam = (metadata as any)?.models?.[uimetadata.config.model]?.hotspots?.exteriorCamera?.messages;
    return FeatureStore.subscribe(
      (s: any) => s.status,
      (status: string) => {
        const featureActive = !!FeatureStore.getState().currentFeatureId;
        if (status === 'play' && featureActive) {
          void setCameraCoreWebgl({ upperRadiusLimit: null, lowerRadiusLimit: null } as any);
        } else if (cam) {
          void setCameraCoreWebgl({
            lowerAlphaLimit: cam.lowerAlphaLimit ?? null,
            lowerBetaLimit: cam.lowerBetaLimit,
            lowerRadiusLimit: cam.lowerRadiusLimit,
            upperAlphaLimit: cam.upperAlphaLimit ?? null,
            upperBetaLimit: cam.upperBetaLimit,
            upperRadiusLimit: cam.upperRadiusLimit,
          } as any);
        }
      },
    );
  }, []);
  // Generic glowMesh support: any interaction whose metadata carries a
  // glowMesh field (currently Sierra's Taillight, see src/utils/glowMesh.ts)
  // gets its material colour swapped whenever that interaction's on/off
  // state changes, regardless of model - driven entirely by metadata, no
  // per-model branching here.
  useEffect(() => {
    const modelId = uimetadata.config.model;
    const modelInteractions = (metadata as any)?.models?.[modelId]?.interactions ?? {};
    return InteractionStore.subscribe(
      (s: any) => s.interactions,
      (newInteractions: Record<string, any>, oldInteractions: Record<string, any>) => {
        for (const id of Object.keys(newInteractions)) {
          const newState = newInteractions[id]?.state;
          if (newState === oldInteractions?.[id]?.state) continue;
          const glowMesh = modelInteractions[id]?.glowMesh;
          if (glowMesh) void applyGlowMesh(glowMesh, !!newState);
        }
      },
    );
  }, []);
  const { packageIDs: appliedPackageIDs } = usePackageStore();
  const { configuration, setConfiguration } = useConfigurationStore();
  const { isConflictsAvailable, conflicts, conflictsTrigger, resolveConflicts, rejectConflicts, isOptionsAvailable, options, optionTrigger, resolveOptions, rejectOptions } = useConflicts();
  const { loadAssets } = useLoadAssets();
  const modelId = uimetadata.config.model;
  // Generic color<->package lock (e.g. CT5 V_Series Platinum Package <-> a
  // specific interior color) - driven entirely by uimetadata, no per-model
  // branching here. See src/hooks/useColorPackageLocks.ts.
  useColorPackageLocks(modelId);

  // ── deferredAssetLoading from metadata ───────────────────────────────────
  const deferredAssetLoading = uimetadata.plugins?.deferredAssetLoading ?? false;
  // Matching Vehicles / inventory lookup — the dealer-selection dialog exists only
  // to power this, so both are gated on the same flag (UAE links only).
  const showLocalMatches = isLocalMatchesEnabled(uimetadata);
  const transitionImage = (uimetadata.config as any).presentation?.transitionImage as string | undefined;

  // ── Loading state ─────────────────────────────────────────────────────────
  // A shared link (?v=… naming a variant this link has) skips the presentation: the
  // recipient wants the configured car, not the intro. Eager / deferred links go
  // straight to the loading screen so the intro never flashes; plain links keep the
  // presentation as their loading screen and enter as soon as the model is loaded.
  const sharedVariantId = getSharedVariantId();
  const skipToLoadingScreen = sharedVariantId !== null && deferredAssetLoading;
  const [showPresentation, setShowPresentation] = useState(!skipToLoadingScreen);
  const [isPresentationFadingOut, setIsPresentationFadingOut] = useState(false);
  const [showSecondaryLoading, setShowSecondaryLoading] = useState(skipToLoadingScreen);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isVariantLoaded, setIsVariantLoaded] = useState(false);
  const [hasEnteredConfigurator, setHasEnteredConfigurator] = useState(false);
  const [initialVariantLoadComplete, setInitialVariantLoadComplete] = useState(false);
  const [showUI, setShowUI] = useState(!deferredAssetLoading);

  // ── Configurator UI state ─────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<TabType>('CONFIG');

  // Auto-correct activeTab when it becomes hidden for the current variant/viewport
  useEffect(() => {
    const variantData = (uimetadata.models as any)[modelId]?.variants?.[variantID] ?? {};
    const tabToggles = variantData.toggleTabs ?? {};
    // Matches the Header's min-[1380px] breakpoint for switching between the mobile
    // bottom nav and the desktop top nav.
    const isMobile = window.innerWidth < 1165;
    const visibleTabs = TABS.filter((tab) =>
      isMobile ? tabToggles[tab]?.mobile !== false : tabToggles[tab]?.desktop !== false,
    );
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      setActiveTab(visibleTabs[0]);
    }
  }, [variantID]); // eslint-disable-line react-hooks/exhaustive-deps

  const { trackEvent } = useLogEvent();
  const { setSticky, fireModuleStart } = useAnalyticsSegmentationContext();
  useLoadingAnalytics();
  useRegisterDefaultSegmentations();

  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMatchesOpen, setIsMatchesOpen] = useState(false);
  const [isSpecDetailsOpen, setIsSpecDetailsOpen] = useState(false);
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [saveDialogSource, setSaveDialogSource] = useState('');
  const openSaveDialog = (source: string) => {
    setSaveDialogSource(source);
    setIsSaveOpen(true);
  };
  const [isShareOpen, setIsShareOpen] = useState(false);
  const [shareSource, setShareSource] = useState('');
  const showShareModal = (source: string) => {
    setShareSource(source);
    setIsShareOpen(true);
  };
  // Phones / tablets share through the OS share sheet (see utils/nativeShare); desktop,
  // browsers without the Web Share API and a refused share fall back to our own modal.
  // Must stay synchronous up to the share call: it needs the tap's user activation.
  const openShareModal = (source: string) => {
    if (!canUseNativeShare()) {
      showShareModal(source);
      return;
    }
    trackEvent(AnalyticsIds.shareInteract, { state: true, parent: source, primaryTypeName: 'nativeShare' });
    // One plain-text message (header + link) and no separate `url`: with a `url`, iOS
    // hands WhatsApp the link as its own item and WhatsApp drops the text, so the
    // "Check out my ... configuration!" header never arrived. `title` is only used by
    // targets that have a subject (Mail).
    void shareNatively({
      title: t('staticTranslations.share.emailSubject', 'Check out my car configuration'),
      text: `${t('staticTranslations.share.whatsappTitle', 'Check out my car configuration!')} ${getShareUrl()}`,
    }).then((result) => {
      if (result === 'unavailable') showShareModal(source);
    });
  };
  const [isDealerOpen, setIsDealerOpen] = useState(false);
  const [selectedDealer, setSelectedDealer] = useState<string | null>(null);

  // Keep the sticky Mixpanel default segmentations in sync with app state that
  // isn't already re-derived every render by useDefaultSegmentations.
  useEffect(() => {
    setSticky({ theme: isDarkMode ? 'dark' : 'light' });
  }, [isDarkMode, setSticky]);
  useEffect(() => {
    setSticky({ currentTab: activeTab });
  }, [activeTab, setSticky]);
  useEffect(() => {
    setSticky({ viewMode: view.type });
  }, [view.type, setSticky]);
  useEffect(() => {
    const m = (uimetadata.models as any)[modelId];
    setSticky({
      modelYear: String(
        m?.variants?.[variantID]?.modelyear ?? (uimetadata.config as any).modelYear ?? ''
      ),
      driveType: m?.variants?.[variantID]?.drivetrain ?? '',
      exteriorColor:
        m?.colors?.exterior?.[exteriorColorID as string]?.label?.en ?? exteriorColorID ?? '',
      interior:
        m?.colors?.interior?.[interiorColorID as string]?.label?.en ?? interiorColorID ?? '',
    });
  }, [modelId, variantID, exteriorColorID, interiorColorID, setSticky]);
  // Wheels aren't a configuration field of their own. A variant's standard wheels are a
  // feature (graphics relevantVariants), an upgrade is an accessory categorised
  // "Alloy Wheels" / "Wheels"; report both, standard wheels first.
  useEffect(() => {
    const isWheel = (text?: string) => /wheel/i.test(text ?? '') && !/steering/i.test(text ?? '');
    const uiModel = (uimetadata.models as any)[modelId];
    const gfxFeatures = (metadata as any)?.models?.[modelId]?.features ?? {};
    const standard = Object.keys(uiModel?.features ?? {})
      .filter((id) => gfxFeatures[id]?.relevantVariants?.includes(variantID))
      .map((id) => uiModel.features[id]?.label?.en as string | undefined)
      .filter((label) => isWheel(label));
    const selected = (accessoriesIDs ?? [])
      .map((id: string) => uiModel?.accessories?.[id])
      .filter((acc: any) => /wheel/i.test(acc?.category?.en ?? ''))
      .map((acc: any) => acc?.label?.en as string | undefined);
    const wheels = [...new Set([...standard, ...selected].filter(Boolean))].join(', ');
    setSticky({ wheels });
  }, [modelId, variantID, accessoriesIDs, setSticky]);
  const [isPackageDetailsOpen, setIsPackageDetailsOpen] = useState(false);
  const [activePackageId, setActivePackageId] = useState<string>('');
  const [matchCount, setMatchCount] = useState(0);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // Track initial config values so we can detect first real user selection
  const initialExtColorRef = useRef<string | null>(null);
  const initialIntColorRef = useRef<string | null>(null);
  const initialAccessoriesRef = useRef<string | null>(null);

  // After dealer is selected, snapshot the "baseline" config
  useEffect(() => {
    if (selectedDealer && !hasUserInteracted) {
      if (initialExtColorRef.current === null) initialExtColorRef.current = exteriorColorID ?? '';
      if (initialIntColorRef.current === null) initialIntColorRef.current = interiorColorID ?? '';
      if (initialAccessoriesRef.current === null) initialAccessoriesRef.current = JSON.stringify(accessoriesIDs ?? []);
    }
  }, [selectedDealer]); // eslint-disable-line react-hooks/exhaustive-deps

  // Detect when user makes a real configuration change after dealer selection
  useEffect(() => {
    if (hasUserInteracted || !selectedDealer) return;
    if (initialExtColorRef.current === null) return;
    const extChanged = exteriorColorID !== initialExtColorRef.current;
    const intChanged = interiorColorID !== initialIntColorRef.current;
    const accChanged = JSON.stringify(accessoriesIDs ?? []) !== initialAccessoriesRef.current;
    if (extChanged || intChanged || accChanged) setHasUserInteracted(true);
  }, [exteriorColorID, interiorColorID, accessoriesIDs, hasUserInteracted, selectedDealer]);

  // Fetch match count from AutoIntel whenever configuration changes
  useEffect(() => {
    if (!showLocalMatches) return;
    const graphicsModel = (metadata as any)?.models?.[modelId];
    const codes: string[] = [];
    if (variantID) {
      const variantRPO = getAutoIntelRPOCode(variantID);
      if (variantRPO) codes.push(variantRPO);
    }
    const extRPO = getAutoIntelRPOCode(exteriorColorID);
    if (extRPO) codes.push(extRPO);
    // Interior colour RPO depends on the model + seat trim — pass variantID to resolve it.
    const intRPO = getAutoIntelRPOCode(interiorColorID, variantID);
    if (intRPO) codes.push(intRPO);
    // Only send options the user actually chose. Defaults must not be sent as features or
    // they wrongly filter inventory. Hidden (display:false) and isDefault items are skipped,
    // plus the current interior colour's seat/belt/stitch/steering defaults (those are
    // display:true & isDefault:false, so they need the interiorConfig lookup).
    const uiModel = (uimetadata.models as any)[modelId];
    const ic = uiModel?.interiorConfig?.[interiorColorID];
    const defaultInteriorIds = new Set<string>();
    if (ic) {
      if (ic.defaultSeatId) defaultInteriorIds.add(ic.defaultSeatId);
      if (ic.steeringWheelId) defaultInteriorIds.add(ic.steeringWheelId);
      Object.values(ic.seats ?? {}).forEach((s: any) => {
        if (s?.seatBelt?.default) defaultInteriorIds.add(s.seatBelt.default);
        if (s?.stitch?.default) defaultInteriorIds.add(s.stitch.default);
      });
    }
    (accessoriesIDs ?? []).forEach((id: string) => {
      const graphicsAcc = graphicsModel?.accessories?.[id];
      if (graphicsAcc?.display === false || graphicsAcc?.isDefault === true) return;
      if (defaultInteriorIds.has(id)) return;
      const rpo = getAutoIntelRPOCode(id);
      if (rpo) codes.push(rpo);
    });
    const allPackageIDs = [...new Set([...(packageIDs ?? []), ...(appliedPackageIDs ?? [])])];
    allPackageIDs.forEach((id: string) => {
      const rpo = getAutoIntelRPOCode(id);
      if (rpo) codes.push(rpo);
    });
    const features = [...new Set(codes)];
    const dealerIds = selectedDealer && DEALER_AUTONTEL_ID_MAP[selectedDealer]
      ? [DEALER_AUTONTEL_ID_MAP[selectedDealer]]
      : undefined;
    getInventoryRecommendations(modelId, features, 9, dealerIds)
      .then((recs) => setMatchCount(recs.length))
      .catch(() => {});
  }, [modelId, variantID, exteriorColorID, interiorColorID, accessoriesIDs, packageIDs, appliedPackageIDs, selectedDealer]);

  // Track previous variant/view for variant-change loader
  const prevVariantRef = useRef<string | undefined>(undefined);
  const [showVariantLoader, setShowVariantLoader] = useState(false);

  // Refs for mutable values accessed inside the stable event callback
  const deferredRef = useRef(deferredAssetLoading);
  const hasEnteredRef = useRef(false);
  const initialLoadCompleteRef = useRef(false);
  // Safety net: force-complete the initial loading screen if VARIANT_LOADED never
  // arrives (e.g. a default accessory the graphics engine never acks with
  // 'AccessoryLoaded'). Fires only when the normal path hasn't completed, so
  // working linkIds that load fast never hit it.
  const loadFallbackRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const completeInitialLoad = useCallback(() => {
    if (initialLoadCompleteRef.current) return;
    if (loadFallbackRef.current) { clearTimeout(loadFallbackRef.current); loadFallbackRef.current = null; }
    setLoadingProgress(100);
    setTimeout(() => {
      setShowSecondaryLoading(false);
      setInitialVariantLoadComplete(true);
      initialLoadCompleteRef.current = true;
      setTimeout(() => setShowUI(true), 300);
    }, 500);
  }, []);

  // ── Graphics events (registered once; uses refs to access latest values) ──
  useEffect(() => {
    CatchEmittedEvents((eventName: string, data?: Record<string, any>) => {
      if (eventName === EventConstants.VARIANT_LOADED) {
        setIsVariantLoaded(true);

        if (deferredRef.current && hasEnteredRef.current && !initialLoadCompleteRef.current) {
          completeInitialLoad();
        }
      } else if (
        eventName === EmitEventErrors.ModelNotFound ||
        eventName === EmitEventErrors.VariantNotFound
      ) {
        trackEvent(AnalyticsIds.modelLoadError, {
          ...getLoadInfo(),
          targetModel: data?.modelID ?? data?.variantID ?? '',
          errorMessage: eventName,
          failingAssetUrl: '',
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Variant-change loader (post first load) ───────────────────────────────
  useEffect(() => {
    if (isLoading) {
      if (!showPresentation && initialVariantLoadComplete) {
        setShowVariantLoader(true);
        // Safety net: never let a variant switch hang forever. If the graphics
        // engine never acks a default accessory for the new variant (so the SDK's
        // loading flag never clears), force-hide the loader after a timeout so the
        // configurator becomes usable. Backward-compatible: fast variants clear
        // well before this fires.
        const t = setTimeout(() => setShowVariantLoader(false), 20000);
        return () => clearTimeout(t);
      }
    } else {
      prevVariantRef.current = variantID;
      setShowVariantLoader(false);
    }
    return undefined;
  }, [isLoading, variantID, showPresentation, initialVariantLoadComplete]);

  // ── Variant-change loader (post first load) ───────────────────────────────
  const handleEnterConfigurator = useCallback(async () => {
    setHasEnteredConfigurator(true);
    hasEnteredRef.current = true;
    fireModuleStart('configurator');

    // Load the model for the presentation-selected journey. Stingray's default
    // (coupe_2lt) matches the boot default from main.tsx, so only GSX needs a
    // switch. Done BEFORE loadAssets() so the correct variant's assets load.
    // Not for a shared link: its variant is authoritative (the journey was aligned to it
    // at boot), and `variantID` here may still be the boot default while the SDK is
    // initialising, which would wrongly swap the shared variant for the journey default.
    const targetVariant = getJourney(getSelectedJourney())?.defaultVariant;
    if (sharedVariantId === null && targetVariant && targetVariant !== variantID) {
      const { setConfiguration } = ConfigurationStore.getState();
      setConfiguration(null);
      const cfg = await useChangeDefaultConfigOnVariantChange(
        uimetadata.config.model,
        targetVariant,
      );
      if (cfg) setConfiguration(cfg);
    }

    if (deferredAssetLoading) {
      // Start loading assets NOW, show secondary loading screen
      loadAssets();
      setLoadingProgress(0);
      setShowSecondaryLoading(true);
      setIsPresentationFadingOut(true);
      setTimeout(() => setShowPresentation(false), 500);

      // Simulate progress to 90% — final jump to 100 happens on VARIANT_LOADED
      const progressInterval = setInterval(() => {
        setLoadingProgress((prev: number) => {
          if (prev < 90) {
            const increment = prev < 30 ? 2 : prev < 60 ? 1 : 0.5;
            return Math.min(prev + increment, 90);
          }
          clearInterval(progressInterval);
          return prev;
        });
      }, 50);

      // Fallback: if VARIANT_LOADED hasn't arrived in 30s, force-complete so the
      // loading screen is never stuck (e.g. an accessory that never acks).
      if (loadFallbackRef.current) clearTimeout(loadFallbackRef.current);
      loadFallbackRef.current = setTimeout(() => completeInitialLoad(), 30000);
    } else {
      // Assets already loaded — just dismiss the presentation
      setIsPresentationFadingOut(true);
      setTimeout(() => {
        setShowPresentation(false);
        setShowUI(true);
        if (showLocalMatches) setIsDealerOpen(true);
      }, 500);
    }
  }, [deferredAssetLoading, loadAssets, completeInitialLoad, showLocalMatches, variantID, sharedVariantId]);

  // Shared link: press "Build Your Ride" for the visitor, once. No waiting on the SDK:
  // the loading screen is already up (eager / deferred), the SDK applies the shared
  // variant whenever it is ready, and VARIANT_LOADED ends the screen. A slow SDK just
  // means a longer wait, never the wrong car. A load that hangs is caught by the same
  // 30 s fallback as a normal Enter. Links that load
  // assets up front keep the presentation as their loading screen
  // and enter once the model is loaded.
  const autoEnteredRef = useRef(false);
  useEffect(() => {
    if (sharedVariantId === null || autoEnteredRef.current) return;
    if (!(deferredAssetLoading || isVariantLoaded)) return;
    autoEnteredRef.current = true;
    void handleEnterConfigurator();
  }, [sharedVariantId, deferredAssetLoading, isVariantLoaded, handleEnterConfigurator]);

  // After secondary loading completes (deferred mode), open dealer dialog —
  // its only purpose is powering Matching Vehicles, so skip it when that's off.
  useEffect(() => {
    if (showLocalMatches && deferredAssetLoading && initialVariantLoadComplete && !showSecondaryLoading) {
      setIsDealerOpen(true);
    }
  }, [deferredAssetLoading, initialVariantLoadComplete, showSecondaryLoading, showLocalMatches]);

  // ── Price calculation ─────────────────────────────────────────────────────
  const totalPrice = useMemo(() => {
    const variantPrice = getPrice((uimetadata.models as any)[modelId]?.variants?.[variantID]?.price);
    const accPrice = (accessoriesIDs ?? []).reduce((sum: number, id: string) => {
      const acc = (uimetadata.models as any)[modelId]?.accessories?.[id];
      if (!acc || acc.isDefault) return sum;
      return sum + getPrice(acc.price);
    }, 0);
    // Packages the user applies land in the package store, not in the
    // configuration — read both, or a priced package (Tahoe has several) never
    // reaches the headline total even though it shows in the summary.
    const allPackageIDs = [...new Set([...(packageIDs ?? []), ...(appliedPackageIDs ?? [])])];
    const pkgPrice = allPackageIDs.reduce((sum: number, id: string) => {
      const pkg = (uimetadata.models as any)[modelId]?.packages?.[id];
      return sum + getPrice(pkg?.price);
    }, 0);
    // Paint and interior trim are chargeable options on the GM price schedule
    // (e.g. Silver Blade Metallic, Habanero interior), so they belong in the
    // headline total like any other option. Gated on `featureFlags.showPrices`:
    // several projects carry non-zero colour prices that were never counted, and
    // switching them on silently would move their headline total.
    const colors = (uimetadata.models as any)[modelId]?.colors;
    const colorPrice =
      getDisplayPrice(colors?.exterior?.[exteriorColorID as string]?.price) +
      getDisplayPrice(colors?.interior?.[interiorColorID as string]?.price);
    return variantPrice + accPrice + pkgPrice + colorPrice;
  }, [variantID, accessoriesIDs, packageIDs, appliedPackageIDs, modelId, exteriorColorID, interiorColorID]);
  useEffect(() => {
    setSticky({ currentPrice: String(totalPrice), matchingVehicleCount: String(matchCount) });
  }, [totalPrice, matchCount, setSticky]);

  const { setAccessoryView } = useAccessoryStore();
  const resetFreeRoam = () => {
    setAccessoryView('free-roam');
    SetActiveAccessory(null);
  };

  // Visible tabs for each viewport — used for Next button labels and navigation
  const desktopVisibleTabs = useMemo(() => getVisibleTabs(variantID, modelId, false), [variantID, modelId]);
  const mobileVisibleTabs  = useMemo(() => getVisibleTabs(variantID, modelId, true),  [variantID, modelId]);

  const handleNext = () => {
    resetFreeRoam();
    if (activeTab === 'SUMMARY') {
      trackEvent(AnalyticsIds.checkoutInteract, {
        state: true,
        parent: 'summary',
        primaryTypeName: String(accessoriesIDs.length + packageIDs.length),
        secondaryTypeName: String(totalPrice),
        tertiaryTypeName: JSON.stringify({
          variantID,
          exteriorColorID,
          interiorColorID,
          accessoriesIDs,
          packageIDs,
        }),
      });
      dispatchAutoIntelEvent(AutoIntelEvents.CONVERSION_SAVE, 'save-build');
      openSaveDialog('summary');
      return;
    }
    const isMobile = window.innerWidth < 1165;
    const navigableTabs = getVisibleTabs(variantID, modelId, isMobile);
    const currentIndex = navigableTabs.indexOf(activeTab);
    if (currentIndex < navigableTabs.length - 1) {
      const destinationTab = navigableTabs[currentIndex + 1];
      trackEvent(AnalyticsIds.navigationInteract, {
        parent: 'nextCta',
        primaryTypeName: destinationTab,
        secondaryTypeName: activeTab,
      });
      setActiveTab(destinationTab);
      setIsPreviewMode(false);
    }
  };

  // ── Switch 3D camera view when EXTERIOR / INTERIOR tab is selected ────────
  useEffect(() => {
    if (!configuration) return;
    const cameFromInterior = view.type === 'interior' || String(view.hotspotId).startsWith('interiorCamera');
    // Coming back from interior, re-apply the canonical exterior camera (incl.
    // its radius limits) from graphics-metadata hotspots.exteriorCamera. The
    // interior camera preset settles asynchronously and leaves a stale radius
    // limit, so the switch's own camera message can get clobbered — re-sending
    // it once the interior state has settled restores the correct exterior view.
    const reapplyExteriorCamera = () => {
      const msgs = (metadata as any)?.models?.[modelId]?.hotspots?.exteriorCamera?.messages;
      if (msgs) setCameraCoreWebgl(msgs);
    };
    if (activeTab === 'EXTERIOR' && view.hotspotId !== 'exteriorCamera') {
      setConfiguration({ ...configuration, view: { type: 'exterior', hotspotId: 'exteriorCamera' } });
      if (cameFromInterior) setTimeout(reapplyExteriorCamera, EXTERIOR_CAMERA_REAPPLY_MS);
    } else if (activeTab === 'INTERIOR' && view.hotspotId !== 'interiorCamera') {
      setConfiguration({ ...configuration, view: { type: 'interior', hotspotId: 'interiorCamera' } });
    } else if (activeTab !== 'EXTERIOR' && activeTab !== 'INTERIOR' && view.type === 'interior') {
      setConfiguration({ ...configuration, view: { type: 'exterior', hotspotId: 'exteriorCamera' } });
      setTimeout(reapplyExteriorCamera, EXTERIOR_CAMERA_REAPPLY_MS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // When this configurator was opened from the offline showroom (same origin),
  // offer a "Back to Showroom" button. The showroom appends ?returnUrl=<its url>;
  // we also fall back to the same-origin referrer (both are on storage.googleapis.com).
  const showroomReturnUrl = useMemo(() => {
    try {
      const q = new URLSearchParams(window.location.search).get('returnUrl');
      if (q) return decodeURIComponent(q);
      const ref = document.referrer;
      if (
        ref &&
        new URL(ref).origin === window.location.origin &&
        /(showroom-offline-assets|\/pwa\/)/.test(ref)
      ) {
        return ref;
      }
    } catch {
      /* ignore */
    }
    return '';
  }, []);

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className={cn(
      "flex flex-col h-dvh overflow-hidden transition-colors duration-300",
      isDarkMode ? "bg-[#0a0a0a] text-white dark" : "bg-white text-black"
    )}>
      {showroomReturnUrl && (
        <button
          onClick={() => {
            window.location.href = showroomReturnUrl;
          }}
          aria-label="Back to showroom"
          style={{
            position: 'fixed',
            top: 10,
            left: 10,
            zIndex: 200,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '6px 12px',
            background: 'rgba(0,0,0,0.72)',
            color: '#fff',
            border: 'none',
            borderRadius: 'var(--brand-button-radius-sm)',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            backdropFilter: 'blur(6px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>←</span>
          <span>Showroom</span>
        </button>
      )}
      <Header
        activeTab={activeTab} 
        setActiveTab={(tab) => {
          resetFreeRoam();
          setActiveTab(tab);
          setIsPreviewMode(false);
        }} 
        price={totalPrice} 
        matches={matchCount}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
        onShowMatches={() => setIsMatchesOpen(true)}
        onShowSpecs={() => {
          trackEvent(AnalyticsIds.specDetailsInteract, {
            state: true,
            primaryTypeName: 'specDetails',
          });
          setIsSpecDetailsOpen(true);
        }}
        modelName={(uimetadata.config as any).modelName ?? (uimetadata.config as any).model}
        modelYear={(uimetadata.config as any).modelYear}
        trimName={(uimetadata.models as any)[modelId]?.variants?.[variantID]?.name?.en}
        drivetrain={(uimetadata.models as any)[modelId]?.variants?.[variantID]?.drivetrain}
        className={showPresentation ? 'invisible pointer-events-none' : ''}
        hasUserInteracted={hasUserInteracted}
      />

      <main className="flex-1 flex flex-col md:flex-row overflow-hidden relative z-[60]">
        {/* Left Section: Viewport */}
        {/* @container: the desktop/mobile split for the bottom controls inside Viewport
            (FreeRoam toggle, Expand/Interactions/Menu row, mobile toolbar) is driven by
            THIS box's own rendered width via Tailwind's @3xl: variant, not by md: (window
            width) - see Viewport.tsx. On an iPad the Config Panel column claims a fixed
            400-480px the moment the window crosses md:, so this box's actual width can be
            far narrower than the window itself, and the old md:-based switch fired long
            before there was room for the desktop layout, overlapping the two. */}
        <div className="h-[40vh] md:h-auto md:flex-1 relative flex flex-col min-h-0 overflow-visible @container">
          <Viewport
            isPreviewMode={isPreviewMode}
            isDarkMode={isDarkMode}
            isLoading={isLoading}
            activeTab={activeTab}
            onChangeBackground={() => {/* TODO: background switcher */}}
            onShare={() => openShareModal('menu')}
            onExploreFeatures={() => {
              trackEvent(AnalyticsIds.featureInteract, {
                parent: 'toggle',
                primaryTypeName: 'exploreFeatures',
                secondaryTypeName: activeTab,
              });
              setIsPreviewMode(true);
            }}
            onBuildAndPrice={() => {
              trackEvent(AnalyticsIds.featureInteract, {
                parent: 'toggle',
                primaryTypeName: 'buildPrice',
                secondaryTypeName: activeTab,
              });
              setIsPreviewMode(false);
            }}
          />
        </div>

        {/* Right Section: Config Panel */}
        <div className={cn(
          "w-full flex-1 min-h-0 md:flex-none md:w-[400px] lg:w-[480px] border-l flex flex-col overflow-hidden transition-colors duration-300",
          isDarkMode ? "border-white/5 bg-[#0f0f0f]" : "border-gray-100 bg-white"
        )}>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <ConfigPanel 
              activeTab={activeTab} 
              isPreviewMode={isPreviewMode}
              setIsPreviewMode={setIsPreviewMode}
              isDarkMode={isDarkMode}
              onPackageInfoClick={(id) => { setActivePackageId(id); setIsPackageDetailsOpen(true); }}
            />
          </div>

          {/* Desktop Footer Actions */}
          <div className={cn(
            "hidden md:flex items-center gap-[11px] px-[11px] border-t transition-colors duration-300 py-[15px]",
            isDarkMode ? "border-white/5 bg-[#0f0f0f]" : "border-[#e6e6e6] bg-white"
          )}>
            {activeTab !== 'SUMMARY' && (
              <button
                onClick={() => {
                  trackEvent(AnalyticsIds.saveBuildInteract, {
                    state: true,
                    parent: activeTab,
                    primaryTypeName: 'lead',
                    secondaryTypeName: 'saveBuild',
                  });
                  dispatchAutoIntelEvent(AutoIntelEvents.CONVERSION_SAVE, 'save-build');
                  openSaveDialog('saveBuild');
                }}
                className={cn(
                  "flex-1 h-[48px] rounded-[var(--brand-cta-radius)] text-[16px] font-bold flex items-center justify-center gap-2 transition-colors border",
                  isDarkMode
                    ? "border-white/20 text-white hover:bg-white/5"
                    : "border-[var(--brand-accent)] text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/5"
                )}>
                {t('staticTranslations.common.saveYourBuild', 'Save Your Build')}
                <Save className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={handleNext}
              className={cn(
                "flex-1 font-bold flex items-center justify-center gap-2 transition-all duration-300",
                activeTab === 'SUMMARY'
                  ? 'font-sans bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white w-full rounded-[var(--brand-cta-radius)] text-[20px] font-bold h-[57px]'
                  : "bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white rounded-[var(--brand-cta-radius)] text-[16px] h-[48px]"
              )}
            >
              <span>{activeTab === 'SUMMARY' ? t('staticTranslations.common.proceed', 'Proceed') : getNextButtonLabel(activeTab, desktopVisibleTabs, t)}</span>
              {activeTab !== 'SUMMARY' && (
                isRTL
                  ? <ChevronLeft className="w-4 h-4 stroke-[2.5]" />
                  : <ChevronRight className="w-4 h-4 stroke-[2.5]" />
              )}
            </button>
          </div>
        </div>
      </main>

      <MobileFooter 
        price={totalPrice} 
        activeTab={activeTab} 
        onNext={handleNext} 
        isPreviewMode={isPreviewMode}
        isDarkMode={isDarkMode}
        onSaveClick={() => {
          trackEvent(AnalyticsIds.saveBuildInteract, {
            state: true,
            parent: activeTab,
            primaryTypeName: 'lead',
            secondaryTypeName: 'saveBuild',
          });
          openSaveDialog('saveBuild');
        }}
        nextTabLabel={activeTab === 'SUMMARY' ? t('staticTranslations.common.proceed', 'Proceed') : getNextButtonLabel(activeTab, mobileVisibleTabs, t)}
      />

      <ConflictDialog
        isOpen={isConflictsAvailable}
        onClose={() => rejectConflicts()}
        onApply={() => resolveConflicts()}
        removedItems={(() => {
          // Like addedItems: when the user REMOVES an accessory/package, the SDK's conflict
          // resolution splices the triggering item out of accessoryIDsGettingRemoved, so the
          // removed box was empty. Include the removal trigger explicitly, then the rest.
          const trg: any = conflictsTrigger;
          const triggerItems: Array<{ id: string; name: string; type: string }> = [];
          if (trg?.type === 'accessory-removal' && trg.accessoryId) {
            triggerItems.push({ id: trg.accessoryId, name: (uimetadata.models as any)[modelId]?.accessories?.[trg.accessoryId]?.label?.en ?? trg.accessoryId, type: 'Accessory' });
          } else if (trg?.type === 'package-removal' && trg.packageId) {
            triggerItems.push({ id: trg.packageId, name: (uimetadata.models as any)[modelId]?.packages?.[trg.packageId]?.label?.en ?? trg.packageId, type: 'Package' });
          }
          const triggerIds = new Set(triggerItems.map((i) => i.id));
          // Structural accessories (e.g. a package's stand-in that only swaps another
          // accessory in/out via defaultAccessory/removeAccessories) carry display:false
          // and an intentionally blank label - 1.0 always filtered these out of every
          // visible list. Do the same here, or they render as an empty bullet.
          const isVisibleAccessory = (id: string) => (metadata as any)?.models?.[modelId]?.accessories?.[id]?.display !== false;
          return [
            ...triggerItems,
            ...((conflicts as any)?.accessoryIDsGettingRemoved ?? []).filter((id: string) => !triggerIds.has(id) && isVisibleAccessory(id)).map((id: string) => ({ id, name: (uimetadata.models as any)[modelId]?.accessories?.[id]?.label?.en ?? id, type: 'Accessory' })),
            ...((conflicts as any)?.packageIDsGettingRemoved ?? []).filter((id: string) => !triggerIds.has(id)).flatMap((id: string) => { const pkg = (uimetadata.models as any)[modelId]?.packages?.[id]; const items: any[] = pkg?.accessoryContent; if (items?.length) return items.map((ac: any) => ({ id, name: ac.accessoryTitle?.en ?? id, type: 'Accessory' })); return [{ id, name: pkg?.label?.en ?? id, type: 'Package' }]; }),
          ];
        })()}
        applyLabel={((conflictsTrigger as any)?.type === 'accessory-removal' || (conflictsTrigger as any)?.type === 'package-removal') ? t('staticTranslations.remove', 'Remove') : undefined}
        addedItems={(() => {
          // NOTE: the SDK's CheckAutoResolveConflicts mutates conflicts.accessoryIDsGettingAdded
          // and splices out the triggering item, so the item being applied is only reliably
          // available via conflictsTrigger. Include it explicitly, then any remaining adds.
          const trg: any = conflictsTrigger;
          const triggerItems: Array<{ id: string; name: string; type: string }> = [];
          if (trg?.type === 'accessory-addition' && trg.accessoryId) {
            triggerItems.push({ id: trg.accessoryId, name: (uimetadata.models as any)[modelId]?.accessories?.[trg.accessoryId]?.label?.en ?? trg.accessoryId, type: 'Accessory' });
          } else if (trg?.type === 'package-addition' && trg.packageId) {
            triggerItems.push({ id: trg.packageId, name: (uimetadata.models as any)[modelId]?.packages?.[trg.packageId]?.label?.en ?? trg.packageId, type: 'Package' });
          }
          const triggerIds = new Set(triggerItems.map((i) => i.id));
          // See removedItems above: skip display:false structural accessories, e.g. a
          // package's stand-in accessory that exists only to swap another one in/out.
          const isVisibleAccessory = (id: string) => (metadata as any)?.models?.[modelId]?.accessories?.[id]?.display !== false;
          return [
            ...triggerItems,
            ...((conflicts as any)?.accessoryIDsGettingAdded ?? []).filter((id: string) => !triggerIds.has(id) && isVisibleAccessory(id)).map((id: string) => ({ id, name: (uimetadata.models as any)[modelId]?.accessories?.[id]?.label?.en ?? id, type: 'Accessory' })),
            ...((conflicts as any)?.packageIDsGettingAdded ?? []).filter((id: string) => !triggerIds.has(id)).map((id: string) => ({ id, name: (uimetadata.models as any)[modelId]?.packages?.[id]?.label?.en ?? id, type: 'Package' })),
          ];
        })()}
        isDarkMode={isDarkMode}
      />

      <OptionsDialog
        isOpen={isOptionsAvailable}
        onClose={() => rejectOptions()}
        onSelect={(option: any) => resolveOptions(option)}
        options={(options as any)?.colorOptions ?? []}
        modelId={modelId}
        currentExteriorColorID={exteriorColorID}
        currentInteriorColorID={interiorColorID}
        isDarkMode={isDarkMode}
        itemLabel={(() => {
          const trg: any = optionTrigger;
          if (trg?.type === 'accessory-addition' || trg?.type === 'accessory-removal') {
            return (uimetadata.models as any)[modelId]?.accessories?.[trg.accessoryId]?.label?.en;
          }
          if (trg?.type === 'package-addition' || trg?.type === 'package-removal') {
            return (uimetadata.models as any)[modelId]?.packages?.[trg.packageId]?.label?.en;
          }
          return undefined;
        })()}
      />

      <PackageDetailsDialog
        isOpen={isPackageDetailsOpen}
        onClose={() => setIsPackageDetailsOpen(false)}
        isSelected={appliedPackageIDs?.includes(activePackageId)}
        onApply={async () => { const { isVariantLoading } = LoaderState.getState(); if (appliedPackageIDs?.includes(activePackageId)) { await RemovePackage(activePackageId, isVariantLoading); } else { await AddPackage(activePackageId, isVariantLoading); } setIsPackageDetailsOpen(false); }}
        packageName={(uimetadata.models as any)[modelId]?.packages?.[activePackageId]?.label?.en ?? ''}
        description={(uimetadata.models as any)[modelId]?.packages?.[activePackageId]?.packageDescription?.en ?? ''}
        accessories={((uimetadata.models as any)[modelId]?.packages?.[activePackageId]?.accessoryContent ?? []).map((ac: any) => ({ name: ac.accessoryTitle?.en ?? '', img: ac.image ? assetSrc(ac.image) : '' }))}
        isDarkMode={isDarkMode}
      />

      <LocalMatchesDialog 
        isOpen={isMatchesOpen}
        onClose={() => setIsMatchesOpen(false)}
        isDarkMode={isDarkMode}
        totalPrice={totalPrice}
        selectedDealer={selectedDealer}
        onDealerChange={(id) => setSelectedDealer(id)}
        onProceed={() => { openSaveDialog('inventory'); }}
      />

      <SpecDetails
        isOpen={isSpecDetailsOpen}
        onClose={() => {
          trackEvent(AnalyticsIds.specDetailsInteract, {
            state: false,
            primaryTypeName: 'specDetails',
          });
          setIsSpecDetailsOpen(false);
        }}
        onBookingClick={() => {
          trackEvent(AnalyticsIds.specSaveInteract, {
            parent: 'specDetails',
            primaryTypeName: 'save',
          });
          setIsSpecDetailsOpen(false);
          dispatchAutoIntelEvent(AutoIntelEvents.CONVERSION_SAVE, 'save-build');
          openSaveDialog('saveBuild');
        }}
        onShareClick={() => { setIsSpecDetailsOpen(false); openShareModal('specDetails'); }}
      />

      <SaveDialog
        isOpen={isSaveOpen}
        onClose={() => setIsSaveOpen(false)}
        isDarkMode={isDarkMode}
        source={saveDialogSource}
      />

      <ShareModal
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        isDarkMode={isDarkMode}
        source={shareSource}
      />

      <DealerSelectorDialog
        isOpen={isDealerOpen}
        onClose={() => setIsDealerOpen(false)}
        onProceed={(dealerId) => {
          setSelectedDealer(dealerId);
          setIsDealerOpen(false);
        }}
        isDarkMode={isDarkMode}
      />

      {/* ── Presentation / LoadingProgress overlay ── */}
      {showPresentation && (
        <div className="fixed inset-0 z-[100]">
          <LoadingProgress
            firstTimeLoad={true}
            // deferredAssetLoading=false → button enabled only when graphics loaded
            // deferredAssetLoading=true  → button enabled immediately
            isLoadingComplete={deferredAssetLoading ? true : isVariantLoaded}
            onEnter={handleEnterConfigurator}
            onSave={() => { dispatchAutoIntelEvent(AutoIntelEvents.CONVERSION_SAVE, 'save-build'); openSaveDialog('saveBuild'); }}
            onShare={() => openShareModal('specDetails')}
            className={isPresentationFadingOut ? 'loading-progress--fade-out' : ''}
          />
        </div>
      )}

      {/* ── Secondary loading screen (deferred mode only) ── */}
      {showSecondaryLoading && (
        <div className="fixed inset-0 z-[101]">
          <LoadingScreen
            type="percentage"
            background="dark"
            fallbackText="Finalizing your experience..."
            progress={loadingProgress}
            transitionImage={transitionImage}
          />
        </div>
      )}

      {/* ── Variant/view change infinite loader ── */}
      {showVariantLoader && (
        <div className="fixed inset-0 z-[102] flex items-center justify-center bg-black/80">
          <div className="flex flex-col items-center gap-4">
            <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            <span className="text-white text-sm font-medium">
              {t('staticTranslations.loadingScreen.variantLoading', 'Variant is loading')}
            </span>
          </div>
        </div>
      )}

      {/* ── Mobile landscape overlay ── */}
      {isMobileLandscape && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 2147483647, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '24px' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.6 }}>
            <rect x="4" y="2" width="16" height="20" rx="2" />
            <line x1="12" y1="18" x2="12" y2="18.01" />
          </svg>
          <div style={{ textAlign: 'center', padding: '0 32px' }}>
            <p style={{ color: '#fff', fontSize: '18px', fontWeight: 700, letterSpacing: '0.05em', margin: 0 }}>Move to Portrait</p>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', marginTop: '8px' }}>for better experience</p>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
